import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Checkbox,
  FormControlLabel,
  Box,
  Autocomplete,
  Chip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { IUser } from '../../types/user';
import { userService } from '../../services/userService';
import { ROLES, IRole } from '../../constants/roles';

interface UserEditDialogProps {
  open: boolean;
  onClose: () => void;
  user: IUser | null;
  onSaved: () => void;
}

interface UserFormData {
  username: string;
  password: string;
  isTfaEnabled: boolean;
  selectedRoles: IRole[];
  accountNumbers: string;
}

const UserEditDialog: React.FC<UserEditDialogProps> = ({
  open,
  onClose,
  user,
  onSaved,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isNew = user === null;

  const getInitialFormData = (): UserFormData => {
    // Map displayRoles back to IRole objects
    let selectedRoles: IRole[] = [];
    if (user?.displayRoles) {
      selectedRoles = user.displayRoles.map((dr) => ({
        id: dr.id,
        itemName: dr.itemName,
      }));
    }

    return {
      username: user?.username || '',
      password: '',
      isTfaEnabled: user?.isTfaEnabled || false,
      selectedRoles,
      accountNumbers: user?.notificationsAllowedAccounts || '',
    };
  };

  const [formData, setFormData] = useState<UserFormData>(getInitialFormData());
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setFormData(getInitialFormData());
      setErrors({});
    }
  }, [open, user]);

  const handleChange = (field: keyof UserFormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user types
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.username) {
      newErrors.username = 'Please provide username';
    }

    // Password is required for new users
    if (isNew && !formData.password) {
      newErrors.password = 'Please provide password';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      // Extract role IDs from selected roles
      const roleIds = formData.selectedRoles.map((role) => role.id);

      if (isNew) {
        await userService.createUser(
          formData.username,
          formData.password,
          formData.isTfaEnabled,
          roleIds,
          formData.accountNumbers
        );
      } else {
        await userService.updateUser(
          formData.username,
          formData.password || undefined,
          formData.isTfaEnabled,
          roleIds,
          formData.accountNumbers
        );
      }

      onSaved();
      onClose();
    } catch (error: any) {
      console.error('Error saving user:', error);
      alert('Network error. Try again later');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="xl" 
      fullWidth
      PaperProps={{
        sx: {
          m: { xs: 1, sm: 2 },
          maxHeight: { xs: '95vh', sm: '90vh' },
          width: { xs: '100%', sm: 'auto', md: '90%', lg: '85%' }
        }
      }}
    >
      <DialogTitle sx={{ pb: { xs: 1, sm: 2 }, fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
        {isNew ? 'Create User' : 'Edit User'}
      </DialogTitle>
      <DialogContent sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 1, sm: 2 }, overflowY: 'auto' }}>
        <Box sx={{ pt: { xs: 1, sm: 2 }, display: 'flex', flexDirection: 'column', gap: { xs: 1.5, sm: 2 } }}>
          <TextField
            fullWidth
            label="Username"
            value={formData.username}
            onChange={(e) => handleChange('username', e.target.value)}
            error={!!errors.username}
            helperText={errors.username}
            disabled={!isNew}
            required
          />

          <TextField
            fullWidth
            type="password"
            label="Password"
            value={formData.password}
            onChange={(e) => handleChange('password', e.target.value)}
            error={!!errors.password}
            helperText={errors.password || (isNew ? '' : 'Leave blank to keep current password')}
            required={isNew}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={formData.isTfaEnabled}
                onChange={(e) => handleChange('isTfaEnabled', e.target.checked)}
              />
            }
            label="Require 2FA"
          />

          <Autocomplete
            multiple
            id="roles-select"
            options={ROLES}
            getOptionLabel={(option) => option.itemName}
            value={formData.selectedRoles}
            onChange={(event, newValue) => {
              handleChange('selectedRoles', newValue);
            }}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            disableCloseOnSelect
            renderInput={(params) => (
              <TextField
                {...params}
                label="Roles"
                placeholder="Select Roles"
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  label={option.itemName}
                  {...getTagProps({ index })}
                  key={option.id}
                />
              ))
            }
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox
                  checked={selected}
                  sx={{ mr: 1 }}
                />
                {option.itemName}
              </li>
            )}
          />

          <TextField
            fullWidth
            multiline
            rows={3}
            label="Selected Accounts (scheduled)"
            value={formData.accountNumbers}
            onChange={(e) => handleChange('accountNumbers', e.target.value)}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 }, flexDirection: { xs: 'column-reverse', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
        <Button 
          onClick={onClose} 
          disabled={loading}
          fullWidth={isMobile}
          sx={{ minWidth: { xs: '100%', sm: 'auto' } }}
        >
          Close
        </Button>
        <Button 
          onClick={handleSave} 
          variant="contained" 
          color="primary" 
          disabled={loading}
          fullWidth={isMobile}
          sx={{ minWidth: { xs: '100%', sm: 'auto' } }}
        >
          {loading ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserEditDialog;
